import React from 'react';
import jsPDF from 'jspdf';

const ReporteEstadoMatriculas = {
  /**
   * Genera un reporte PDF del estado de matrículas
   * @param {Array} datosMatriculas - Array de datos de matrículas
   * @param {Object} filtros - Filtros aplicados
   * @param {string} filtros.sucursal - Sucursal filtrada
   * @param {string} filtros.estadoEntrega - Estado de entrega filtrado
   * @param {string} filtros.busquedaChasis - Búsqueda por chasis
   */
  generarReporte: (datosMatriculas, filtros = {}) => {
    const doc = new jsPDF('landscape'); // Orientación horizontal para más columnas
    
    // Configuración del documento
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    
    // Nombre de la empresa
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(localStorage.getItem("nombrenegocio") || 'WebMotors', pageWidth / 2, 15, { align: 'center' });
    
    // Título del reporte
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('REPORTE DE ESTADO DE MATRÍCULAS', pageWidth / 2, 25, { align: 'center' });
    
    // Línea separadora
    doc.setLineWidth(0.5);
    doc.line(margin, 30, pageWidth - margin, 30);
    
    // Información de filtros aplicados
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Fecha de generación: ' + new Date().toLocaleDateString(), pageWidth / 2, 40, { align: 'center' });
    
    let yPosition = 50;
    
    // Mostrar filtros aplicados
    if (filtros.sucursal && filtros.sucursal !== 'Todas') {
      doc.text(`Sucursal filtrada: ${filtros.sucursal}`, margin, yPosition);
      yPosition += 8;
    }
    
    if (filtros.estadoEntrega && filtros.estadoEntrega !== 'Todas') {
      doc.text(`Estado de entrega: ${filtros.estadoEntrega}`, margin, yPosition);
      yPosition += 8;
    }
    
    if (filtros.busquedaChasis && filtros.busquedaChasis.trim() !== '') {
      doc.text(`Búsqueda por chasis: ${filtros.busquedaChasis}`, margin, yPosition);
      yPosition += 8;
    }
    
    // Estadísticas
    const total = datosMatriculas.length;
    const entregadas = datosMatriculas.filter(item => item.estadoEntrega === 'Entregada').length;
    const noEntregadas = datosMatriculas.filter(item => item.estadoEntrega === 'No Entregada').length;
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('ESTADÍSTICAS:', margin, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Total de matrículas: ${total}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Entregadas: ${entregadas}`, margin, yPosition);
    yPosition += 6;
    doc.text(`No entregadas: ${noEntregadas}`, margin, yPosition);
    yPosition += 15;
    
    // Crear tabla manual
    const headers = ['#', 'Chasis', 'Placa', 'Local', 'Estado', 'Fecha Entrega', 'Recibido Por', 'Cédula', 'Sucursal Entrega', 'Cajero', 'Endoso'];
    const columnWidths = [12, 35, 20, 12, 20, 25, 40, 25, 30, 25, 15];
    const startX = margin;
    let currentY = yPosition;
    
    // Dibujar encabezados
    doc.setFontSize(6);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(41, 128, 185);
    
    let currentX = startX;
    headers.forEach((header, index) => {
      doc.setFillColor(41, 128, 185);
    
      doc.rect(currentX, currentY, columnWidths[index], 8, 'F');
      doc.setTextColor(255, 255, 255);
      
      doc.text(header, currentX + 1, currentY + 5);
      currentX += columnWidths[index];
    });
    
    currentY += 8;
    
    // Dibujar datos
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    
    datosMatriculas.forEach((item, index) => {
      // Verificar si necesitamos una nueva página
      if (currentY > pageHeight - 30) {
        doc.addPage();
        currentY = 20;
      }
      
      const rowData = [
        (index + 1).toString(),
        item.chasis || 'N/A',
        item.placa || 'Sin placa',
        item.sucursal || 'N/A',
        item.estadoEntrega || 'N/A',
        item.fechaEntrega ? new Date(item.fechaEntrega).toLocaleDateString() : 'No entregada',
        `${item.nombreRecibio || ''} ${item.apellidoRecibio || ''}`.trim() || 'N/A',
        item.cedulaRecibio || 'N/A',
        item.sucursalEntrega || 'N/A',
        item.cajero || 'N/A',
        item.endoso || 'No'
      ];
      
      currentX = startX;
      rowData.forEach((data, colIndex) => {
        // Dibujar borde de celda
        doc.rect(currentX, currentY, columnWidths[colIndex], 8);
        
        // Truncar texto si es muy largo
        let displayText = data.toString();
        if (displayText.length > 15) {
          displayText = displayText.substring(0, 12) + '...';
        }
        
        doc.text(displayText, currentX + 2, currentY + 5);
        currentX += columnWidths[colIndex];
      });
      
      currentY += 8;
    });
    
    // Pie de página
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text('Este reporte muestra el estado actual de las matrículas en el sistema.', pageWidth / 2, currentY + 10, { align: 'center' });
    
    // Generar y descargar el PDF
    const nombreArchivo = `Reporte_Estado_Matriculas_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(nombreArchivo);
  },

  /**
   * Genera un reporte PDF y retorna la URL para usar en un modal
   * @param {Array} datosMatriculas - Array de datos de matrículas
   * @param {Object} filtros - Filtros aplicados
   * @returns {string} URL del PDF generado
   */
  generarReporteParaModal: (datosMatriculas, filtros = {}) => {
    
    
    const doc = new jsPDF('landscape');
    
    // Configuración del documento
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    
    // Nombre de la empresa
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(localStorage.getItem("nombrenegocio") || 'WebMotors', pageWidth / 2, 15, { align: 'center' });
    
    // Título del reporte
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('REPORTE DE ESTADO DE MATRÍCULAS', pageWidth / 2, 25, { align: 'center' });
    
    // Línea separadora
    doc.setLineWidth(0.5);
    doc.line(margin, 30, pageWidth - margin, 30);
    
    // Información de filtros aplicados
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Fecha de generación: ' + new Date().toLocaleDateString(), pageWidth / 2, 40, { align: 'center' });
    
    let yPosition = 50;
    
    // Mostrar filtros aplicados
    if (filtros.sucursal && filtros.sucursal !== 'Todas') {
      doc.text(`Sucursal filtrada: ${filtros.sucursal}`, margin, yPosition);
      yPosition += 8;
    }
    
    if (filtros.estadoEntrega && filtros.estadoEntrega !== 'Todas') {
      doc.text(`Estado de entrega: ${filtros.estadoEntrega}`, margin, yPosition);
      yPosition += 8;
    }
    
    if (filtros.busquedaChasis && filtros.busquedaChasis.trim() !== '') {
      doc.text(`Búsqueda por chasis: ${filtros.busquedaChasis}`, margin, yPosition);
      yPosition += 8;
    }
    
    // Estadísticas
    const total = datosMatriculas.length;
    const entregadas = datosMatriculas.filter(item => item.estadoEntrega === 'Entregada').length;
    const noEntregadas = datosMatriculas.filter(item => item.estadoEntrega === 'No Entregada').length;
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('ESTADÍSTICAS:', margin, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Total de matrículas: ${total}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Entregadas: ${entregadas}`, margin, yPosition);
    yPosition += 6;
    doc.text(`No entregadas: ${noEntregadas}`, margin, yPosition);
    yPosition += 15;
    
    // Crear tabla manual
    const headers = ['#', 'Chasis', 'Placa', 'Local', 'Estado', 'Fecha Entrega', 'Recibido Por', 'Cédula', 'Sucursal Entrega', 'Cajero', 'Endoso'];
    const columnWidths = [12, 35, 20, 12, 20, 25, 40, 25, 30, 25, 15];
    const startX = margin;
    let currentY = yPosition;
    
    // Dibujar encabezados
    doc.setFontSize(6);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(41, 128, 185);
    
    let currentX = startX;
    headers.forEach((header, index) => {
      doc.setFillColor(41, 128, 185);
    
      doc.rect(currentX, currentY, columnWidths[index], 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(header, currentX + 1, currentY + 5);
      currentX += columnWidths[index];
    });
    
    currentY += 8;
    
    // Dibujar datos
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    
    datosMatriculas.forEach((item, index) => {
      // Verificar si necesitamos una nueva página
      if (currentY > pageHeight - 30) {
        doc.addPage();
        currentY = 20;
      }
      
      const rowData = [
        (index + 1).toString(),
        item.chasis || 'N/A',
        item.placa || 'Sin placa',
        item.sucursal || 'N/A',
        item.estadoEntrega || 'N/A',
        item.fechaEntrega ? new Date(item.fechaEntrega).toLocaleDateString() : 'No entregada',
        `${item.nombreRecibio || ''} ${item.apellidoRecibio || ''}`.trim() || 'N/A',
        item.cedulaRecibio || 'N/A',
        item.sucursalEntrega || 'N/A',
        item.cajero || 'N/A',
        item.endoso || 'No'
      ];
      
      currentX = startX;
      rowData.forEach((data, colIndex) => {
        // Dibujar borde de celda
        doc.rect(currentX, currentY, columnWidths[colIndex], 8);
        
        // Truncar texto si es muy largo
        let displayText = data.toString();
        if (displayText.length > 15) {
          displayText = displayText.substring(0, 12) + '...';
        }
        
        doc.text(displayText, currentX + 2, currentY + 5);
        currentX += columnWidths[colIndex];
      });
      
      currentY += 8;
    });
    
    // Pie de página
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text('Este reporte muestra el estado actual de las matrículas en el sistema.', pageWidth / 2, currentY + 10, { align: 'center' });
    
    // Convertir PDF a blob y crear URL
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    return pdfUrl;
  }
};

export default ReporteEstadoMatriculas;